import {
  Box,
  Button,
  Chip,
  Grid2,
  LinearProgress,
  Typography,
} from "@mui/material";
import PrimaryIcon from "Components/Common/PrimaryIcon";
import React, { useEffect } from "react";
import { ReactComponent as AchievementIcon } from "Assets/Icons/Curriculum/Achievement.svg";
import CircularProgressWithLabel from "Components/Common/CircularProgressWithLabel";
import ModuleProgress from "./ModuleProgress";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getModuleScore } from "Slices/thunks";

const styles = (theme) => ({
  "& .analyticsBox": {
    border: `1px solid ${theme.palette.border.blue}`,
    borderRadius: "8px",
    padding: "16px",
  },
});

const ModuleAnalytics = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  
  const { Modules, ModuleScore, Translation } = useSelector((state) => ({
    Modules: state.Course.modules,
    ModuleScore: state.Assignment.moduleScore,
    Translation: state.Translation.translation?.Course || {},
  }));

  useEffect(() => {
    if (Array.isArray(Modules)) {
      const module = Modules[id]
      dispatch(getModuleScore({ModuleID: module?.ID}))
    }

  }, [Modules, id])

  return (
    <Grid2 sx={styles}>
      <Box display={"flex"} alignItems={"center"} gap="10px">
        <Typography>{`${Translation?.Module} - ${parseInt(id) + 1}`}</Typography>
        <Chip
          variant="outlined"
          outlined
          color="yellow"
          label={
            <Typography fontWeight={500} color="secondary">
              {" "}
              {`${Modules[id]?.Week} ${Translation?.Weeks}`} 
            </Typography>
          }
        />
      </Box>
      <Typography mt="5px" display={"flex"} fontWeight={500} variant="subtitle">
        {Modules[id]?.ModuleName}
      </Typography>
      <Typography
        display={"flex"}
        alignItems={"center"}
        gap="5px"
        mt="8px"
        variant="body2"
        color="secondary.light"
      >
        <PrimaryIcon icon={AchievementIcon} />
        {Modules[id]?.ModuleDescription}
      </Typography>
      <Box mt="30px">
        <ModuleProgress module={Modules[id]} />
      </Box>
      <Grid2
        mt={"20px"}
        container
        spacing={2}
        columns={{ xl: 12, lg: 12, md: 6, sm: 6 }}
      >
        <Grid2
          size={6}
          spacing={2}
          container
          columns={{ xl: 12, lg: 12, md: 12, sm: 6 }}
        >
          <Grid2 className="analyticsBox" size={6}>
            <Typography fontWeight={500} variant="subtitle">
              {Translation?.ModulePSP}
            </Typography>
            <Box mt="16px" sx={{ width: "fit-content" }}>
              <CircularProgressWithLabel
                variant="body2"
                color="primary"
                fontWeight={400}
                value={ModuleScore?.PSP || 0}
                size={45}
                label={(ModuleScore?.PSP || 0).toFixed(2)}
              />
            </Box>
          </Grid2>
          <Grid2 className="analyticsBox" size={6}>
            <Typography fontWeight={500} variant="subtitle">
              {Translation?.ModuleAttendance}
            </Typography>
            <Box mt="16px" sx={{ width: "fit-content" }}>
              <CircularProgressWithLabel
                variant="body2"
                color="primary"
                fontWeight={400}
                value={ModuleScore?.Attendance || 0}
                size={45}
                label={(ModuleScore?.Attendance || 0).toFixed(2)}
              />
            </Box>
          </Grid2>
          <Grid2 className="analyticsBox" size={6}>
            <Typography fontWeight={500} variant="subtitle">
              {Translation?.ResearchPaper}
            </Typography>
            <Typography mt="12px" mb="12px" color="success">
              {`0 ${Translation?.Of} 2 ${Translation?.Done}`}
            </Typography>
            <Button variant="outlined" color="secondary">
              {Translation?.ViewAll}
            </Button>
          </Grid2>
          <Grid2 className="analyticsBox" size={6}>
            <Typography fontWeight={500} variant="subtitle">
              {Translation?.HowYourScoreCalculated}
            </Typography>
            <Box mt="16px">
              <Typography mb="4px">{`${Translation?.ModulePSP} - ${Modules[id]?.PSP}%`}</Typography>
              <LinearProgress color="purple" value={Modules[id]?.PSP} variant="determinate" />
              <Typography mb="4px" mt="8px">
                {`${Translation?.ExamMockInterview} - ${Modules[id]?.Exam}%`}
              </Typography>
              <LinearProgress
                color="success"
                value={Modules[id]?.Exam}
                variant="determinate"
              />
              <Typography mt="10px" variant="body2" color="secondary.light">
                {Translation?.ModulePSPAssignments}
              </Typography>
            </Box>
          </Grid2>
        </Grid2>
        <Grid2 className="analyticsBox" size={6}>
          <Typography fontWeight={500} variant="subtitle">
            {Translation?.EarnCreditWorkloadHours}
          </Typography>
          <Box display={"flex"} flexDirection={"column"} gap={"5px"} mt="16px">
            <Typography>{`${Translation?.ToEarnCredits}  -`}</Typography>
            <Typography color="secondary.light">{`1. ${Translation?.AttendLectures}`}</Typography>
            <Typography color="secondary.light">
              {`2. ${Translation?.SolveAssignmentsAdditionalProblems}`}
            </Typography>
            <Typography color="secondary.light">
              {`3. ${Translation?.ReadResearchPapers}`}
            </Typography>
            <Typography color="secondary.light">
              {`4. ${Translation?.PassTestsMockInterviews}`}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="secondary"
            sx={{ marginTop: "20px" }}
          >
            {Translation?.ViewWorkloadProgress}
          </Button>
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default React.memo(ModuleAnalytics);
